import type { JSX } from 'react';
import { Link } from 'react-router-dom';
import { Mail, Calendar, Trash2 } from 'lucide-react';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { ApplicationStatusSelect } from './ApplicationStatusSelect';
import type {
  JobPositionCandidateWithDetails,
  ApplicationStatus,
} from '../../types/jobPositionCandidate.types';

export interface JobPositionCandidateCardProps {
  candidate: JobPositionCandidateWithDetails;
  onStatusChange: (candidateId: string, status: ApplicationStatus) => void;
  onUnlink: (candidateId: string, candidateName: string) => void;
  isUpdating: boolean;
}

function formatDate(dateString: string): string {
  return new Date(dateString).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

function CandidateCardNotes({ notes }: { notes?: string | null }): JSX.Element {
  if (notes) return <p className="text-muted-foreground">{notes}</p>;
  return <span className="text-muted-foreground">-</span>;
}

export function JobPositionCandidateCard({
  candidate,
  onStatusChange,
  onUnlink,
  isUpdating,
}: JobPositionCandidateCardProps): JSX.Element {
  const { candidate: info } = candidate;
  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <div className="flex flex-col gap-1">
          <Link
            to={`/candidates/${info.id}`}
            className="font-semibold text-lg text-primary hover:underline"
          >
            {info.name}
          </Link>
          <p className="text-sm text-muted-foreground flex items-center gap-1">
            <Mail className="h-3 w-3" />
            {info.email}
          </p>
        </div>
        <Button
          variant="ghost"
          size="icon"
          onClick={() => onUnlink(info.id, info.name)}
          title="Unlink candidate"
        >
          <Trash2 className="h-4 w-4 text-destructive" />
        </Button>
      </CardHeader>
      <CardContent>
        <div className="flex flex-col gap-2 text-sm">
          <ApplicationStatusSelect
            currentStatus={candidate.applicationStatus}
            onStatusChange={(newStatus) => onStatusChange(info.id, newStatus)}
            disabled={isUpdating}
          />
          <div className="flex items-center gap-1 text-muted-foreground">
            <Calendar className="h-3 w-3" />
            {formatDate(candidate.appliedAt)}
          </div>
          <CandidateCardNotes notes={candidate.notes} />
        </div>
      </CardContent>
    </Card>
  );
}
